import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../AxiosInstance";
import useReconnectingWebSocket from "../../../../../hooks/useReconnectingWebSocket";
import styles from "./logs_section.module.css";

const MAX_LINES = 1000;

export default function LogsSection({ pdui_id }) {
  const { t } = useTranslation();

  const SERVICES = [
    { id: "", label: t('logs.allServices') },
    { id: "kpdu", label: "KPDU (firmware)" },
    { id: "celery", label: "Celery" },
    { id: "mqtt", label: "MQTT" },
    { id: "server", label: "Server" },
  ];

  const [lines, setLines] = useState([]);
  const [service, setService] = useState("");
  const [linesCount, setLinesCount] = useState(200);
  const [requesting, setRequesting] = useState(false);
  const [following, setFollowing] = useState(false);
  const [message, setMessage] = useState(null);
  const viewerRef = useRef(null);
  const followIntervalRef = useRef(null);
  const autoScrollRef = useRef(true);

  // Recibe los logs_response que la nube reenvía desde el equipo. Reconecta
  // solo si se cae (deploy, red), que antes dejaba la sección muerta.
  const listeningForResponse = useReconnectingWebSocket({
    path: `/ws/client/pdui_${pdui_id}_logs/`,
    pduiId: Number(pdui_id),
    label: "WebSocket de logs",
    onMessage: (data) => {
      if (data.type !== "remote_mgmt_message" || data.topic !== "logs_response") return;
      const payload = data.payload || {};
      if (Array.isArray(payload.lines)) {
        setLines((prev) => {
          if (prev.length === 0) {
            // First batch: replace
            return payload.lines;
          }
          // Follow mode: find new lines by comparing last known line
          const lastKnown = prev[prev.length - 1];
          const newIdx = payload.lines.indexOf(lastKnown);
          const newLines = newIdx >= 0
            ? payload.lines.slice(newIdx + 1)
            : payload.lines; // if not found, show all (device restarted?)

          if (newLines.length === 0) return prev;

          const merged = [...prev, ...newLines];
          return merged.length > MAX_LINES ? merged.slice(-MAX_LINES) : merged;
        });
        setMessage({ type: "success", text: t('logs.linesReceived', { count: payload.lines.length }) });
      }
      setRequesting(false);
    },
  });

  // Auto-scroll only if user is at the bottom
  useEffect(() => {
    if (viewerRef.current && autoScrollRef.current) {
      viewerRef.current.scrollTop = viewerRef.current.scrollHeight;
    }
  }, [lines]);

  const handleScroll = () => {
    if (!viewerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = viewerRef.current;
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 40;
  };

  const doRequest = useCallback(async () => {
    try {
      await axiosInstance.post(`pdui/${pdui_id}/request-logs/`, {
        service,
        lines: linesCount,
      });
      return true;
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.error || t('logs.error') });
      setFollowing(false);
      return false;
    }
  }, [pdui_id, service, linesCount, t]);

  const requestLogs = async () => {
    setRequesting(true);
    setLines([]);
    setMessage({ type: "info", text: t('logs.requesting') });
    const sent = await doRequest();
    if (!sent) {
      // El error del POST ya está en pantalla; no taparlo con "enviada".
      setRequesting(false);
      return;
    }
    setMessage({ type: "info", text: t('logs.sentWaiting') });
    setTimeout(() => {
      setRequesting((prev) => {
        if (prev) setMessage({ type: "warning", text: t('logs.timeout') });
        return false;
      });
    }, 30000);
  };

  // Follow mode: auto-request every 5 seconds
  const toggleFollow = () => {
    if (following) {
      // Stop
      setFollowing(false);
      if (followIntervalRef.current) {
        clearInterval(followIntervalRef.current);
        followIntervalRef.current = null;
      }
      setMessage(null);
    } else {
      // Start: do first request immediately, then every 15s.
      //
      // Cada pedido hace que el equipo lea el journal y publique la respuesta
      // completa por MQTT. A 5 s se apilaban respuestas grandes en el único
      // worker de ingesta de la nube y los logs terminaban no llegando nunca.
      setFollowing(true);
      setLines([]);
      autoScrollRef.current = true;
      setMessage({ type: "info", text: t('logs.followActive') });
      doRequest();
      followIntervalRef.current = setInterval(() => {
        doRequest();
      }, 15000);
    }
  };

  // Cleanup interval on unmount or service/lines change
  useEffect(() => {
    return () => {
      if (followIntervalRef.current) {
        clearInterval(followIntervalRef.current);
      }
    };
  }, []);

  // Stop follow when service or lines count changes
  useEffect(() => {
    if (following) {
      setFollowing(false);
      if (followIntervalRef.current) {
        clearInterval(followIntervalRef.current);
        followIntervalRef.current = null;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, linesCount]);

  const clear = () => {
    setLines([]);
    setMessage(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>{t('logs.title')}</div>
        <div className={styles.controls}>
          <select
            className={styles.select}
            value={service}
            onChange={(e) => setService(e.target.value)}
            disabled={requesting || following}
          >
            {SERVICES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
          <select
            className={styles.select}
            value={linesCount}
            onChange={(e) => setLinesCount(Number(e.target.value))}
            disabled={requesting || following}
          >
            <option value={50}>50 {t('logs.lines')}</option>
            <option value={100}>100 {t('logs.lines')}</option>
            <option value={200}>200 {t('logs.lines')}</option>
            <option value={500}>500 {t('logs.lines')}</option>
          </select>
          <button
            className={`${styles.button} ${styles.buttonOrange}`}
            onClick={requestLogs}
            disabled={requesting || following}
          >
            {requesting ? t('logs.waiting') : t('logs.requestLogs')}
          </button>
          <button
            className={`${styles.button} ${following ? styles.buttonFollow : ""}`}
            onClick={toggleFollow}
            disabled={requesting}
          >
            {following ? t('logs.stopFollow') : t('logs.follow')}
          </button>
          <button className={styles.button} onClick={clear} disabled={requesting || following}>
            {t('logs.clear')}
          </button>
        </div>
      </div>

      <div className={styles.info}>
        <span className={`${styles.statusDot} ${listeningForResponse ? styles.statusDotOn : styles.statusDotOff}`}></span>
        {listeningForResponse ? "WebSocket conectado" : "WebSocket desconectado"}
        {following && <span className={styles.followBadge}> LIVE</span>}
        {message && ` - ${message.text}`}
      </div>

      <div className={styles.logViewer} ref={viewerRef} onScroll={handleScroll}>
        {lines.length === 0 && (
          <div className={styles.empty}>
            {t('logs.emptyState')}
          </div>
        )}
        {lines.map((line, idx) => {
          const isError = /ERROR|error|Exception|Traceback/i.test(line);
          const isWarning = /WARNING|warn/i.test(line);
          const isDebug = /DEBUG/i.test(line);
          const levelClass = isError ? styles.logError
            : isWarning ? styles.logWarning
            : isDebug ? styles.logDebug
            : "";

          // Parse: "2026-04-17T10:41:16-0500 KPDU-Stracontech service[pid]: message"
          const match = line.match(/^(\S+)\s+\S+\s+(\w+)\[\d+\]:\s*(.*)$/);

          return (
            <div key={idx} className={`${styles.logLine} ${levelClass}`}>
              {match ? (
                <>
                  <span className={styles.logTimestamp}>{match[1]}</span>
                  <span className={styles.logService}>[{match[2]}]</span>
                  {match[3]}
                </>
              ) : line}
            </div>
          );
        })}
      </div>
    </div>
  );
}
